// Infraestructura i18n mínima y lista para crecer.
// Idioma por defecto: español (es). Idiomas preparados: es, en, de, fr, ru, pt.
// Los textos existentes de la app siguen en español; este sistema se usa para
// TODO el contenido nuevo (capa de Desarrollo) y para futuros idiomas.
import { createContext, useContext, useState, useCallback, useMemo } from "react";

export const SUPPORTED_LOCALES = ["es", "en", "de", "fr", "ru", "pt"];
export const DEFAULT_LOCALE = "es";

// Recursos. 'es' es la base; los idiomas sin traducción recurren a 'es'.
const RESOURCES = {
    es: {
        "dev.nav": "Mi desarrollo",
        "dev.hub.title": "Personal & Leadership Development",
        "dev.hub.subtitle": "Un único sistema para crecer tú y hacer crecer a tu equipo.",
        "dev.hub.soon": "Próximamente",
        "dev.hub.empty.title": "Aún no hay módulos activados",
        "dev.hub.empty.body": "Cuando se activen los módulos de desarrollo aparecerán aquí.",
        "dev.module.personalMastery": "Personal Mastery",
        "dev.module.lifeBalance": "Equilibrio de vida",
        "dev.module.goals": "Objetivos",
        "dev.module.values": "Valores",
        "dev.module.habits": "Hábitos",
        "dev.module.mindset": "Mentalidad",
        "dev.module.socialSkills": "Habilidades sociales",
        "dev.module.socialChallenges": "Retos sociales",
        "dev.module.conversationAI": "Práctica con IA",
        "dev.module.leadershipAcademy": "Academia de liderazgo",
        "dev.module.teamDevelopment": "Desarrollo de equipo",
        "dev.module.teamGrowthSimulator": "Simulador de crecimiento",
        "dev.progression": "Yo · Mi equilibrio · Mis objetivos · Mi comunicación · Mi liderazgo · Mi equipo · Crecimiento sostenible",

        "temp.caliente": "Caliente",
        "temp.templado": "Templado",
        "temp.frio": "Frío",
        "gam.streak": "Racha",
        "gam.days": "días",
        "gam.level": "Nivel",
        "gam.best": "Mejor",
        "gam.achievements": "Logros",
        "gam.unlocked": "desbloqueados",
        "gam.xp_to_next": "XP para el siguiente nivel",
        "metrics.title": "Métricas del pipeline",
        "metrics.conversion": "Tasa de conversión",
        "metrics.active": "Activos",
        "metrics.converted": "Convertidos",
        "metrics.discarded": "Descartados",
        "settings.language": "Idioma",
        "settings.language.help": "Idioma de la interfaz para las secciones nuevas.",
    },
    en: {
        "dev.nav": "My development",
        "dev.hub.title": "Personal & Leadership Development",
        "dev.hub.subtitle": "One system to grow yourself and grow your team.",
        "dev.hub.soon": "Coming soon",
        "dev.hub.empty.title": "No modules enabled yet",
        "dev.hub.empty.body": "Enabled development modules will appear here.",
        "dev.module.teamGrowthSimulator": "Growth Simulator",
        "dev.progression": "You · Your balance · Your goals · Your communication · Your leadership · Your team · Sustainable growth",

        "temp.caliente": "Hot",
        "temp.templado": "Warm",
        "temp.frio": "Cold",
        "gam.streak": "Streak",
        "gam.days": "days",
        "gam.level": "Level",
        "gam.best": "Best",
        "gam.achievements": "Achievements",
        "gam.unlocked": "unlocked",
        "gam.xp_to_next": "XP to next level",
        "metrics.title": "Pipeline metrics",
        "metrics.conversion": "Conversion rate",
        "metrics.active": "Active",
        "metrics.converted": "Converted",
        "metrics.discarded": "Discarded",
        "settings.language": "Language",
        "settings.language.help": "Interface language for the new sections.",
    },
};

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
    const [locale, setLocaleState] = useState(() => {
        const saved = typeof window !== "undefined" ? window.localStorage.getItem("nm_locale") : null;
        return SUPPORTED_LOCALES.includes(saved) ? saved : DEFAULT_LOCALE;
    });

    const setLocale = useCallback((next) => {
        if (!SUPPORTED_LOCALES.includes(next)) return;
        window.localStorage.setItem("nm_locale", next);
        setLocaleState(next);
    }, []);

    const t = useCallback(
        (key, fallback) => {
            const table = RESOURCES[locale] || {};
            if (table[key] != null) return table[key];
            if (RESOURCES[DEFAULT_LOCALE][key] != null) return RESOURCES[DEFAULT_LOCALE][key];
            return fallback != null ? fallback : key;
        },
        [locale]
    );

    const value = useMemo(() => ({ locale, setLocale, t, locales: SUPPORTED_LOCALES }), [locale, setLocale, t]);
    return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useT() {
    const ctx = useContext(LocaleContext);
    if (!ctx) throw new Error("useT debe usarse dentro de LocaleProvider");
    return ctx;
}
